/**
 * La forgemagie (GDD §19). Module pur.
 *
 * Le jeu fabrique des pièces à **jet aléatoire** : deux exemplaires de la même
 * amulette n'ont pas les mêmes chiffres. Jusqu'ici on ne pouvait que
 * recommencer — refaire la recette et espérer mieux. La forgemagie donne
 * l'autre voie : garder la pièce et la pousser, ligne par ligne.
 *
 * Trois décisions structurent le module.
 *
 * **Elle se paie en kamas, pas en runes.** C'est contre-intuitif pour qui
 * connaît Dofus, mais les Pépites et Tourmalines du broyage servent déjà à 301
 * et 97 recettes (GDD §8) : les taxer ralentirait le craft au lieu de vider les
 * coffres. Or c'est exactement le problème qu'on traite — mesuré, un compte de
 * niveau 200 gagne 1,39 M de kamas par heure, quand la totalité des
 * emplacements de personnage n'en coûte que 32,85 M, soit une journée de jeu.
 * Après quoi la monnaie ne servait plus à rien.
 *
 * **Le coût explose près du maximum.** Un puits à prix fixe se remplit ; celui-ci
 * ne peut pas, parce que le dernier point d'une ligne coûte des ordres de
 * grandeur de plus que le premier. C'est aussi ce qui rend une pièce parfaite
 * remarquable au lieu d'inévitable.
 *
 * **Un échec fait chuter une autre ligne.** Sans cela, forger ne serait qu'un
 * distributeur : payer jusqu'à gagner. La chute rend la perfection réellement
 * difficile — et c'est précisément ce qui donnera de la valeur à une pièce
 * forgée le jour où elle s'échangera entre joueurs.
 */
import type { Item } from "./contenu.ts"
import type { Porte } from "./objets.ts"

/** Une ligne forgeable : un effet dont la fourchette n'est pas figée. */
export interface Ligne {
  effet: number
  min: number
  max: number
  /** valeur actuelle sur cet exemplaire */
  valeur: number
  /** ce qu'une tentative réussie ajoute */
  pas: number
  /** avancement dans la fourchette, de 0 à 1 ; plafonné à 1 en surpuissance */
  position: number
  /** points gagnés **au-dessus** du maximum naturel */
  surpuissance: number
  /** points sacrifiés **sous** le minimum naturel */
  malediction: number
  /** la valeur la plus haute atteignable, surpuissance comprise */
  plafond: number
  /** la valeur la plus basse atteignable, malédiction comprise */
  plancher: number
}

/**
 * Douze paliers par ligne, quelle que soit sa largeur.
 *
 * Une Vitalité qui va de 401 à 450 offre quarante-neuf points : les monter un
 * par un demanderait quarante-neuf clics pour une seule ligne d'une seule
 * pièce. Le pas est donc proportionnel, et toutes les lignes se perfectionnent
 * en une douzaine de coups.
 */
export const PALIERS = 12

export const pasDe = (min: number, max: number) =>
  Math.max(1, Math.round((max - min) / PALIERS))

/** Les lignes qu'on peut pousser sur cette pièce. */
export function lignes(it: Item, porte: Porte): Ligne[] {
  return it.effets
    .filter((e) => e.max > e.min)
    .map((e) => {
      const plafond = plafondDe(e.max)
      const plancher = plancherDe(e.min, e.max)
      const valeur = Math.min(plafond, Math.max(plancher, porte.jet[e.effet] ?? e.min))
      return {
        effet: e.effet, min: e.min, max: e.max, valeur,
        pas: pasDe(e.min, e.max),
        position: Math.max(0, Math.min(1, (valeur - e.min) / (e.max - e.min))),
        surpuissance: Math.max(0, valeur - e.max),
        malediction: Math.max(0, e.min - valeur),
        plafond, plancher,
      }
    })
}

/**
 * Jusqu'où la surpuissance peut monter : vingt pour cent au-dessus du maximum.
 *
 * Ce n'est pas ce qui freine — le coût s'en charge, en doublant et demi à chaque
 * marche. C'est une sécurité pour le combat : au-delà, l'écart de niveau (§4)
 * cesserait de vouloir dire quelque chose et l'on pourrait tout écraser.
 */
export const PLAFOND_SURPUISSANCE = 0.2

export const plafondDe = (max: number) => Math.round(max * (1 + PLAFOND_SURPUISSANCE))

/**
 * Jusqu'où une ligne peut **descendre**, sous son minimum.
 *
 * Exactement autant qu'elle peut monter : ce qu'on sacrifie d'un côté, on peut
 * l'espérer de l'autre. La symétrie n'est pas qu'une élégance, c'est ce qui rend
 * l'échange lisible sans lire une documentation.
 */
export const plancherDe = (min: number, max: number) => min - (plafondDe(max) - max)

/** La ligne est-elle à son maximum **naturel** ? */
export const auMaximum = (l: Ligne) => l.valeur >= l.max

/** Et à son plafond absolu, surpuissance comprise ? Là, il n'y a plus rien à faire. */
export const auPlafond = (l: Ligne) => l.valeur >= l.plafond

/**
 * Ce que coûte la prochaine tentative sur une ligne.
 *
 * `niveau^1,7` suit à peu près la valeur de la pièce, et le facteur en
 * `1/(1 - position)` fait diverger le dernier palier.
 *
 * Calibré sur le revenu mesuré — 1,39 M de kamas par heure à niveau 200 avec
 * six personnages, 340 k à niveau 50. Pousser à fond **une** ligne d'une pièce
 * de niveau 200 coûte alors quelques heures de jeu, et perfectionner la pièce
 * entière quelques dizaines. Un compte en porte quatre-vingt-seize : le plafond
 * n'est pas fait pour être atteint, il est fait pour qu'il en reste toujours.
 */
export const COUT_BASE = 1.2

/**
 * Ce que coûte une marche de surpuissance par rapport à la précédente.
 *
 * Deux et demi : la première marche au-dessus du maximum coûte deux fois et
 * demie la dernière marche normale, la deuxième six fois, la cinquième cent
 * fois. C'est ce qui rend le puits réellement sans fond, et c'est aussi ce qui
 * fait qu'une pièce à cinq marches de surpuissance se remarque.
 */
export const FACTEUR_SURPUISSANCE = 2.5

export function coutForge(it: Item, l: Ligne): number {
  if (auPlafond(l)) return 0
  // On borne la position sous 1 : à la dernière marche, `1 - position` vaut
  // `pas / (max - min)` et non zéro, donc le coût reste fini.
  const reste = Math.max(l.pas / (l.max - l.min), 1 - l.position)
  const base = COUT_BASE * Math.pow(it.niveau, 1.7) / reste
  if (l.surpuissance <= 0) return Math.max(1, Math.round(base))
  const marches = Math.ceil(l.surpuissance / l.pas)
  return Math.max(1, Math.round(base * Math.pow(FACTEUR_SURPUISSANCE, marches)))
}

/**
 * Les chances de réussite, de 90 % en bas de fourchette à 20 % en haut.
 *
 * Le joueur voit ce chiffre avant de payer : la tentative doit être un pari
 * assumé, jamais une surprise.
 */
export const CHANCE_BASSE = 0.9
export const CHANCE_HAUTE = 0.2

/** Au-dessus du maximum, la chance ne dépend plus de l'avancement : elle est fixe. */
export const CHANCE_SURPUISSANCE = 0.15

export const chanceForge = (l: Ligne): number =>
  l.surpuissance > 0
    ? CHANCE_SURPUISSANCE
    : CHANCE_BASSE - (CHANCE_BASSE - CHANCE_HAUTE) * l.position

/**
 * **Le puits** : ce qui rend la forgemagie stratégique et non pas seulement
 * chère.
 *
 * Jusqu'ici la surpuissance ne coûtait que des kamas. Assez pour être un puits
 * sans fond, pas assez pour être une décision : avec le temps, tout le monde
 * finissait par tout monter. Le puits impose un **échange** — chaque cran
 * au-dessus d'un maximum se paie d'un cran arraché sous un minimum, ailleurs sur
 * la même pièce.
 *
 * C'est la logique de Dofus, et surtout c'est ce qui donne aux pièces une
 * personnalité : une amulette monstrueuse en Vitalité et catastrophique en
 * Sagesse n'est pas une amulette « meilleure », c'est un choix. Deux joueurs
 * partis du même objet n'arrivent pas au même endroit — et c'est exactement ce
 * qui vaut d'être vendu à l'Hôtel des Ventes.
 */
export const PUITS_BASE = 2

/**
 * Ce qu'il reste de marge sur cette pièce.
 *
 * Positif : on peut encore surpuissancer. Nul : il faut d'abord maudire une
 * autre ligne. On compte en **crans**, pas en points : une ligne large monte par
 * bonds de quatre, une ligne étroite d'un seul, et compter en points ferait
 * payer trois fois plus cher la première.
 */
export function puits(it: Item, porte: Porte): number {
  let credit = PUITS_BASE
  for (const l of lignes(it, porte)) {
    credit += Math.ceil(l.malediction / l.pas)
    credit -= Math.ceil(l.surpuissance / l.pas)
  }
  return credit
}

/**
 * Ce que coûte de maudire une ligne d'un cran.
 *
 * Bien moins cher que de la monter, et **sans échec possible** : abîmer une
 * pièce à dessein n'a pas à être un pari. Ce n'est pas gratuit pour autant —
 * sinon la première chose à faire sur toute pièce serait de tout maudire.
 */
export function coutMalediction(it: Item): number {
  return Math.max(1, Math.round(COUT_BASE * Math.pow(it.niveau, 1.7) * 0.4))
}

export const auPlancher = (l: Ligne) => l.valeur <= l.plancher

/** Ce qu'une tentative a produit. */
export type Issue =
  | { type: "reussite"; effet: number; gain: number; surpuissance: boolean }
  | { type: "echec"; effet: number; chute: number | null; perte: number }
  | { type: "maudite"; effet: number; perte: number; credit: number }
  | { type: "impossible"; raison: "kamas" | "maximum" | "introuvable" | "puits" | "plancher" }

export interface Tentative {
  porte: Porte
  kamas: number
  issue: Issue
}

/**
 * Tente de pousser une ligne.
 *
 * Le tirage est fourni par l'appelant : le module reste pur, et la même graine
 * rejoue la même tentative — ce dont le serveur aura besoin le jour où il
 * arbitrera l'économie.
 */
export function forger(
  it: Item, porte: Porte, effet: number, kamas: number, rng: () => number,
): Tentative {
  const toutes = lignes(it, porte)
  const l = toutes.find((x) => x.effet === effet)
  if (!l) return { porte, kamas, issue: { type: "impossible", raison: "introuvable" } }
  if (auPlafond(l)) return { porte, kamas, issue: { type: "impossible", raison: "maximum" } }

  const cout = coutForge(it, l)
  if (kamas < cout) return { porte, kamas, issue: { type: "impossible", raison: "kamas" } }

  const surpuissance = l.valeur >= l.max
  // Au-dessus du maximum, il faut de la marge : c'est le puits (voir plus haut).
  if (surpuissance && puits(it, porte) <= 0) {
    return { porte, kamas, issue: { type: "impossible", raison: "puits" } }
  }

  const reste = kamas - cout
  const jet = { ...porte.jet }

  if (rng() < chanceForge(l)) {
    const avant = l.valeur
    // Sous le maximum, une marche s'y arrête net. Le franchir se paie au prix
    // de la surpuissance et se décide donc en connaissance de cause : sans ce
    // butoir, une marche ordinaire déborderait au-dessus et l'on obtiendrait
    // gratuitement ce qui devrait coûter deux fois et demie plus cher.
    jet[effet] = surpuissance
      ? Math.min(l.plafond, l.valeur + l.pas)
      : Math.min(l.max, l.valeur + l.pas)
    return {
      porte: { ...porte, jet },
      kamas: reste,
      issue: { type: "reussite", effet, gain: jet[effet]! - avant, surpuissance },
    }
  }

  // **Au-dessus du maximum, l'échec ne coûte que la surpuissance.**
  //
  // On perd une marche, jamais plus bas que le maximum naturel : ce qui a été
  // payé une fois au prix fort est acquis pour de bon. Sans ce plancher, tenter
  // la surpuissance reviendrait à risquer une pièce qu'on a mis des heures à
  // finir, et personne ne tenterait jamais. Les autres lignes ne sont pas
  // touchées non plus — la surpuissance est une affaire entre la ligne et soi.
  if (surpuissance) {
    const bas = Math.max(l.max, l.valeur - l.pas)
    jet[effet] = bas
    return {
      porte: { ...porte, jet },
      kamas: reste,
      issue: { type: "echec", effet, chute: l.valeur > bas ? effet : null, perte: l.valeur - bas },
    }
  }

  // L'échec fait chuter **une autre** ligne, tirée parmi celles qui peuvent
  // encore descendre.
  //
  // Quand il n'y en a plus aucune, la tentative échoue sans rien retirer. Faire
  // chuter la ligne visée elle-même paraissait plus sévère, et c'était surtout
  // faux : à 20 % de réussite en haut de fourchette, une marche gagnée pour
  // quatre perdues donne une dérive négative — le sommet devenait inatteignable
  // quel que soit le temps passé. Le prix reste le coût réel, la chute reste le
  // coût stratégique.
  //
  // **Une ligne déjà au maximum ne chute jamais.** Sans ce verrou, pousser la
  // dernière ligne rabat celles qu'on venait de finir, et perfectionner une
  // pièce entière devient impossible quel que soit le budget — mesuré, deux
  // cent mille tentatives sans converger. Or c'est cette pièce-là qui vaudra
  // d'être vendue le jour où le marché ouvrira. Le sommet doit rester cher,
  // pas interdit.
  const candidates = toutes.filter((x) => x.effet !== effet && x.valeur > x.min && x.valeur < x.max)
  const victime = candidates.length > 0
    ? candidates[Math.floor(rng() * candidates.length)]!
    : null
  if (!victime) return { porte, kamas: reste, issue: { type: "echec", effet, chute: null, perte: 0 } }

  const bas = Math.max(victime.min, victime.valeur - victime.pas)
  jet[victime.effet] = bas
  return {
    porte: { ...porte, jet },
    kamas: reste,
    issue: { type: "echec", effet, chute: victime.effet, perte: victime.valeur - bas },
  }
}

/**
 * Maudit une ligne. Toujours réussi : on sait ce qu'on fait, c'est le problème.
 *
 * En deux temps, parce que « sacrifier une ligne » et « creuser sous son
 * minimum » ne sont pas le même geste.
 *
 * **Tant que la ligne est dans sa fourchette**, la maudire l'abandonne : elle
 * tombe d'un coup à son minimum. Rien n'est crédité — on n'a encore rien
 * sacrifié, seulement renoncé. Faire descendre cran par cran à travers toute la
 * fourchette aurait demandé douze clics pour un résultat identique, et douze
 * clics qui n'apportent rien ne sont pas une décision.
 *
 * **Sous le minimum**, chaque cran rend une marge. C'est là qu'on paie vraiment.
 */
export function maudire(
  it: Item, porte: Porte, effet: number, kamas: number,
): Tentative {
  const l = lignes(it, porte).find((x) => x.effet === effet)
  if (!l) return { porte, kamas, issue: { type: "impossible", raison: "introuvable" } }
  if (auPlancher(l)) return { porte, kamas, issue: { type: "impossible", raison: "plancher" } }

  const cout = coutMalediction(it)
  if (kamas < cout) return { porte, kamas, issue: { type: "impossible", raison: "kamas" } }

  const bas = l.valeur > l.min ? l.min : Math.max(l.plancher, l.valeur - l.pas)
  const jet = { ...porte.jet, [effet]: bas }
  return {
    porte: { ...porte, jet },
    kamas: kamas - cout,
    issue: { type: "maudite", effet, perte: l.valeur - bas, credit: bas < l.min ? 1 : 0 },
  }
}

/** Ce que la prochaine malédiction fera à cette ligne : renoncer, ou creuser. */
export const abandonne = (l: Ligne) => l.valeur > l.min
